import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { Activity as ActivityIcon, CircleAlert, Loader2, RotateCcw } from "lucide-react";
import type { DriveActivity } from "@/api/driveApi";
import { useActivityFeed } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { formatDayHeading } from "@/lib/format";
import { PageTitle } from "../DriveView";
import { EmptyState } from "../EmptyState";
import { ActivityRow } from "./activityText";

/**
 * /activity — what happened across the user's Drive: their own items
 * (whoever did it), things they did, and items shared with them. Grouped
 * by day, newest first, loading older pages as the list scrolls.
 */
export function ActivityPage() {
    const auth = useAuth();
    const me = auth.user?.profile.email;
    const navigate = useNavigate();
    const feed = useActivityFeed();
    const scrollerRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);

    const groups = useMemo(() => {
        const out: { key: string; label: string; rows: DriveActivity[] }[] = [];
        for (const a of feed.data?.pages.flatMap((p) => p.items) ?? []) {
            const key = new Date(a.createdAt).toDateString();
            const last = out[out.length - 1];
            if (last && last.key === key) last.rows.push(a);
            else out.push({ key, label: formatDayHeading(a.createdAt), rows: [a] });
        }
        return out;
    }, [feed.data]);

    // Infinite scroll: fetch the next page as the sentinel nears the viewport.
    const { hasNextPage, isFetchingNextPage, fetchNextPage } = feed;
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !hasNextPage) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
            },
            { root: scrollerRef.current, rootMargin: "400px 0px" }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage, groups.length]);

    const open = (a: DriveActivity) => navigate(`/open/${encodeURIComponent(a.itemId)}`);

    let content;
    if (feed.isPending) {
        content = (
            <div className="space-y-5 pt-4" aria-busy="true" aria-label="Loading activity">
                {Array.from({ length: 8 }, (_, i) => (
                    <div key={i} className="flex gap-3 px-2">
                        <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
                        <div className="flex-1 space-y-2 pt-1">
                            <div className="h-3 animate-pulse rounded bg-muted" style={{ width: `${45 + ((i * 13) % 40)}%` }} />
                            <div className="h-2.5 w-24 animate-pulse rounded bg-muted" />
                        </div>
                    </div>
                ))}
            </div>
        );
    } else if (feed.isError) {
        content = (
            <EmptyState icon={CircleAlert} tone="rose" title="Couldn't load activity" description={feed.error.message}>
                <Button variant="outline" onClick={() => void feed.refetch()}>
                    <RotateCcw className="h-4 w-4" /> Try again
                </Button>
            </EmptyState>
        );
    } else if (groups.length === 0) {
        content = (
            <EmptyState
                icon={ActivityIcon}
                title="No activity yet"
                description="Uploads, edits, moves, sharing and deletions in your Drive — and on files shared with you — show up here."
            />
        );
    } else {
        content = (
            <div className="mx-auto max-w-3xl pb-10">
                {groups.map((g) => (
                    <section key={g.key} aria-labelledby={`day-${g.key}`} className="mb-4">
                        <h2
                            id={`day-${g.key}`}
                            className="sticky top-0 z-10 bg-background/95 px-2 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur"
                        >
                            {g.label}
                        </h2>
                        <ol className="space-y-0.5">
                            {g.rows.map((a) => (
                                <li key={a.id}>
                                    <ActivityRow activity={a} me={me} onOpen={open} />
                                </li>
                            ))}
                        </ol>
                    </section>
                ))}
                <div ref={sentinelRef} aria-hidden className="h-px" />
                {hasNextPage ? (
                    <div className="flex justify-center py-4">
                        <Button variant="ghost" size="sm" disabled={isFetchingNextPage} onClick={() => void fetchNextPage()}>
                            {isFetchingNextPage ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            {isFetchingNextPage ? "Loading…" : "Load older activity"}
                        </Button>
                    </div>
                ) : (
                    <p className="py-6 text-center text-xs text-muted-foreground">
                        That's everything — activity is kept for 180 days.
                    </p>
                )}
            </div>
        );
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 px-3 pt-3 sm:px-6">
                <div className="flex min-h-11 items-center gap-2">
                    <div className="min-w-0 flex-1">
                        <PageTitle icon={ActivityIcon}>Activity</PageTitle>
                    </div>
                </div>
            </div>
            <div ref={scrollerRef} className="min-h-0 flex-1 overflow-auto px-2 sm:px-6">
                {content}
            </div>
        </div>
    );
}
